/**
 * Greeks and the T+0 curve for one portfolio group — ROADMAP 6.6 applied.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FORWARD HAS TO COME FROM SOMEWHERE, AND IT IS NOT ALWAYS THERE
 *
 * Both outputs need each leg's implied volatility, which needs a FORWARD for
 * that expiry. 6.6 gets one from the chain's at-the-money straddle; this
 * screen loads no chain.
 *
 * What it does have is the position itself, and a position often contains a
 * call and a put at the SAME strike — a straddle is exactly that, and so is
 * any structure with a matched pair. Put-call parity then recovers the
 * forward the market is using, self-consistently:
 *
 *     F = K + (C - P) x e^(rT)
 *
 * When there is no matched pair — a strangle, most condors — there is no
 * forward, and the honest answer is NONE. Not the spot: feeding the cash
 * index to Black-76 reads every call high and every put low by the basis,
 * which here would print a greek with a sign that can be wrong on a small
 * delta and a T+0 curve sitting visibly off a position the trader can check
 * against their broker.
 *
 * So this returns a tagged result and the screen draws what it was given.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import {
  black76Price,
  forwardFromParity,
  legGreeks,
  yearsToExpiry,
  type OptionType,
} from '@apna/shared'
import { RISK_FREE_RATE } from '@/screens/terminal/useChainGreeks'
import { estimateMargin, istDateOf, type MarginEstimate } from '@apna/shared'
import type { PositionRow } from './portfolioModel'

/** One leg, priced. */
interface SolvedLeg {
  readonly netQty: number
  readonly strike: number
  readonly optionType: OptionType
  readonly iv: number
}

export type GroupGreeks =
  | {
      readonly kind: 'NONE'
      /** Why, for a tooltip. */
      readonly why: string
    }
  | {
      readonly kind: 'OK'
      /** Position greeks: PER POSITION, lot size already in `netQty`. */
      readonly delta: number
      readonly gamma: number
      readonly theta: number
      readonly vega: number
      readonly forward: number
      readonly years: number
      /**
       * Initial margin this position is blocking right now, estimated by
       * the exchange's method — SPAN scan + exposure (`margin.ts`).
       *
       * Solved in the SAME pass as the greeks, off the same forward, the
       * same clock and the same per-leg volatilities. Computed separately
       * it would scan a slightly different underlying from the one the
       * delta beside it describes.
       */
      readonly margin: MarginEstimate
      /**
       * Value of the whole group if SPOT were here, today rather than at
       * expiry. Null when there is no live spot to anchor the basis to.
       */
      readonly valueAt: ((spot: number) => number) | null
    }

/** A call and a put at one strike, if the group has such a pair. */
function parityPair(rows: readonly PositionRow[]): { strike: number; call: PositionRow; put: PositionRow } | null {
  const calls = new Map<number, PositionRow>()
  const puts = new Map<number, PositionRow>()
  for (const r of rows) {
    if (r.strike === null) continue
    if (r.optionType === 'CE') calls.set(r.strike, r)
    else if (r.optionType === 'PE') puts.set(r.strike, r)
  }
  for (const [strike, call] of calls) {
    const put = puts.get(strike)
    if (put) return { strike, call, put }
  }
  return null
}

export function groupGreeks(
  rows: readonly PositionRow[],
  ltpOf: (token: number | null) => number | null,
  /** The underlying's live price. Null when unknown; greeks still work. */
  spot: number | null,
  now: Date = new Date(),
): GroupGreeks {
  const open = rows.filter((r) => r.open && r.strike !== null && (r.optionType === 'CE' || r.optionType === 'PE'))
  if (open.length === 0) return { kind: 'NONE', why: 'Nothing open to measure.' }

  const expiry = open[0]!.expiry
  if (expiry === null) return { kind: 'NONE', why: 'No expiry on these legs.' }
  const years = yearsToExpiry(new Date(expiry), now)
  if (years <= 0) return { kind: 'NONE', why: 'Expired; greeks are no longer defined.' }

  const pair = parityPair(open)
  if (pair === null) {
    return {
      kind: 'NONE',
      why: 'No call and put at the same strike here, so there is no forward to solve against. Using the spot instead would misprice both sides by the basis.',
    }
  }
  const callPrice = ltpOf(pair.call.token)
  const putPrice = ltpOf(pair.put.token)
  if (callPrice === null || putPrice === null) {
    return { kind: 'NONE', why: 'The matched pair has no price yet.' }
  }

  const forward = forwardFromParity({
    callPrice,
    putPrice,
    strike: pair.strike,
    years,
    rate: RISK_FREE_RATE,
  })
  if (forward === null) return { kind: 'NONE', why: 'The matched pair does not imply a usable forward.' }

  const solved: SolvedLeg[] = []
  let delta = 0
  let gamma = 0
  let theta = 0
  let vega = 0

  for (const r of open) {
    const price = ltpOf(r.token)
    if (price === null) {
      // ONE unpriced leg and the position greeks are not the position's.
      // A delta missing a leg is worse than no delta: it reads as a hedge
      // ratio someone would trade against.
      return { kind: 'NONE', why: 'A leg has no price yet, so the position greeks would be missing it.' }
    }
    const g = legGreeks({
      price,
      forward,
      strike: r.strike as number,
      years,
      rate: RISK_FREE_RATE,
      optionType: r.optionType as OptionType,
    })
    if (g.kind !== 'OK') return { kind: 'NONE', why: `A leg could not be solved: ${g.why}` }

    solved.push({
      netQty: r.netQty,
      strike: r.strike as number,
      optionType: r.optionType as OptionType,
      iv: g.iv,
    })
    // Scaled by the SIGNED quantity, which already carries units and
    // direction. Per-unit greeks times units is the position's.
    delta += g.greeks.delta * r.netQty
    gamma += g.greeks.gamma * r.netQty
    theta += g.greeks.theta * r.netQty
    vega += g.greeks.vega * r.netQty
  }

  /*
   * The T+0 value at a hypothetical SPOT.
   *
   * The chart's x axis is spot; Black-76 wants a forward. The two differ by
   * the BASIS, which is carry and does not move as the underlying is scanned
   * across a chart, so the basis measured now is held and the forward moves
   * one for one with spot.
   *
   * That needs a live spot to measure the basis FROM. Without one the curve
   * would have to assume F = S, which is the cash-index substitution this
   * module's header refuses — so `valueAt` is null instead and the chart
   * draws the expiry line alone.
   *
   * Each leg is repriced at its OWN implied volatility, held fixed. That is
   * what a T+0 line means; it is not a claim the surface will not move.
   */
  const basis = spot !== null && spot > 0 ? forward - spot : null
  const entryOf = new Map(
    open.map((r) => [`${r.strike}|${r.optionType}`, r.avgPrice] as const),
  )

  const valueAt =
    basis === null
      ? null
      : (at: number): number => {
          let total = 0
          for (const leg of solved) {
            const entry = entryOf.get(`${leg.strike}|${leg.optionType}`)
            if (entry === undefined) continue
            const price = black76Price({
              forward: at + basis,
              strike: leg.strike,
              years,
              rate: RISK_FREE_RATE,
              vol: leg.iv,
              optionType: leg.optionType,
            })
            total += (price - entry) * leg.netQty
          }
          return Math.round(total * 100) / 100
        }

  /*
   * `isExpiryDay` from the legs' own expiry against today, because the
   * exchange's extra 2% lands on the day the contract settles — and this
   * screen is read on that day more than any other.
   */
  const margin = estimateMargin({
    legs: solved.map((l) => ({
      netQty: l.netQty,
      strike: l.strike,
      optionType: l.optionType,
      iv: l.iv,
      price: ltpOf(open.find((r) => r.strike === l.strike && r.optionType === l.optionType)?.token ?? null) ?? 0,
    })),
    forward,
    spot: spot !== null && spot > 0 ? spot : forward,
    years,
    rate: RISK_FREE_RATE,
    isExpiryDay: istDateOf(now.getTime()) === expiry.slice(0, 10),
  })

  return { kind: 'OK', delta, gamma, theta, vega, forward, years, valueAt, margin }
}

/** `Δ -0.12 · Θ +1,240 · V -312`, or null when there is nothing to show. */
export function greeksLabel(g: GroupGreeks): string | null {
  if (g.kind !== 'OK') return null
  const signed = (v: number, digits: number): string =>
    `${v >= 0 ? '+' : '-'}${Math.abs(v).toLocaleString('en-IN', {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}`
  return `Δ ${signed(g.delta, 2)} · Θ ${signed(g.theta, 0)} · V ${signed(g.vega, 0)}`
}
